import { useCallback } from 'react';
import { create } from 'zustand';

/**
 * Toast minimalista (zustand — DEC-015, sem dependência nova). `useToast()` devolve uma função
 * `toast(msg, tone)`; o `<Toaster/>` desenha a pilha e é montado uma vez no shell (AppLayout).
 */
export type ToastTone = 'default' | 'warn' | 'danger';

interface ToastItem {
  id: string;
  msg: string;
  tone: ToastTone;
}

interface ToastState {
  items: ToastItem[];
  push: (msg: string, tone: ToastTone) => void;
  remove: (id: string) => void;
}

const useToastStore = create<ToastState>()((set) => ({
  items: [],
  push: (msg, tone) => {
    const id = Math.random().toString(36).slice(2);
    set((s) => ({ items: [...s.items, { id, msg, tone }] }));
    window.setTimeout(() => set((s) => ({ items: s.items.filter((t) => t.id !== id) })), 3400);
  },
  remove: (id) => set((s) => ({ items: s.items.filter((t) => t.id !== id) })),
}));

/** Hook de conveniência: `const toast = useToast(); toast('mensagem', 'warn');`. */
export function useToast() {
  const push = useToastStore((s) => s.push);
  return useCallback((msg: string, tone: ToastTone = 'default') => push(msg, tone), [push]);
}

const DOT_BY_TONE: Record<ToastTone, string> = {
  default: 'var(--vega-accent)',
  warn: 'var(--vega-warn)',
  danger: 'var(--vega-danger)',
};

/** Host visual dos toasts. Montar uma vez no shell. */
export function Toaster() {
  const items = useToastStore((s) => s.items);
  return (
    // Achado F5 (revisão 2026-07): sem role/aria-live, leitor de tela nunca anunciava os toasts —
    // inclusive o aviso do conflito 412 (F1). role="alert" (danger) interrompe na hora; "status"
    // (default/warn) anuncia de forma educada, sem cortar o que já estava sendo lido.
    <div className="pointer-events-none fixed bottom-4 left-1/2 z-[1000] flex -translate-x-1/2 flex-col-reverse gap-1.5">
      {items.map((t) => (
        <div
          key={t.id}
          role={t.tone === 'danger' ? 'alert' : 'status'}
          aria-live={t.tone === 'danger' ? 'assertive' : 'polite'}
          className="pointer-events-auto flex items-center gap-2 rounded bg-vega-ink px-3.5 py-2 text-[12.5px] font-medium text-vega-bg shadow-vega-lg"
        >
          <span
            className="h-1.5 w-1.5 shrink-0 rounded-full"
            style={{ background: DOT_BY_TONE[t.tone] }}
          />
          {t.msg}
        </div>
      ))}
    </div>
  );
}
